import { default as React } from 'react';
import {
  Page,
  Header,
  Content,
  InfoCard,
  Link,
} from '@backstage/core-components';
import { Box, Grid, Typography } from '@material-ui/core';
import { styled } from '@mui/material/styles';
import StorageIcon from '@mui/icons-material/Storage';
import TimelineIcon from '@mui/icons-material/Timeline';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HomeIcon from '@mui/icons-material/Home';

// Styled Section Box
const SectionBox = styled(Box)(({ theme }) => ({
  margin: theme.spacing(3, 0),
}));

// Quick Action Card
function QuickActionCard({ title, description, icon, link }: any) {
  return (
    <InfoCard
      title={
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          {icon}
          <Typography variant="h6">{title}</Typography>
        </Box>
      }
      deepLink={{ link, title: 'Open' }}
      divider
    >
      <Typography variant="body2">{description}</Typography>
    </InfoCard>
  );
}

export const homePage = () => {
  return (
    <Page themeId="home">
      {/* Hero / Header */}
      <Header
        title="Welcome to SkyStage"
        subtitle="WellSky’s Developer Portal for Visibility, Acceleration, and Innovation"
      />

      <Content>
        <Box maxWidth="lg" mx="auto">
          {/* Quick Actions */}
          <SectionBox>
            <Typography variant="h5" gutterBottom>
              🚀 Quick Actions
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <QuickActionCard
                  title="Software Catalog"
                  description="Explore all services and components"
                  icon={<StorageIcon color="primary" />}
                  link="/catalog"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <QuickActionCard
                  title="Create Project"
                  description="Spin up a new service instantly"
                  icon={<RocketLaunchIcon color="primary" />}
                  link="/create"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <QuickActionCard
                  title="AI Project Catalog"
                  description="Discover reusable AI-powered apps"
                  icon={<HomeIcon color="primary" />}
                  link="/create"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <QuickActionCard
                  title="Operational Metrics"
                  description="See uptime, issues, and security"
                  icon={<TimelineIcon color="primary" />}
                  link="/catalog"
                />
              </Grid>
            </Grid>
          </SectionBox>

          {/* What is SkyStage */}
          <SectionBox>
            <InfoCard title="🌐 What is SkyStage?" divider>
              <Typography paragraph>
                SkyStage is WellSky’s internal developer portal, built on the
                Backstage framework. It provides a centralized, authoritative
                view of our software ecosystem—what software we have, how it’s
                organized, and how it’s performing. It also serves as a
                launchpad for new projects, enabling one-click creation and
                secure hosting of services and AI-powered applications.
              </Typography>
              <Typography>
                SkyStage is a strategic enabler for WellSky’s FY26 Technology
                MBOs, supporting platform modernization, automation, and
                AI-first development.
              </Typography>
            </InfoCard>
          </SectionBox>

          {/* Why SkyStage Matters */}
          <SectionBox>
            <InfoCard title="💡 Why SkyStage Matters" divider>
              <Box mb={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Source of Truth for Software Assets
                </Typography>
                <ul>
                  <li>
                    Visualizes WellSky’s software hierarchy—from components to
                    solutions to business units.
                  </li>
                  <li>
                    Aligns engineering, business, and compliance teams around a
                    shared hierarchy and values (in collaboration with Jolinda
                    Lambert and David Stevens).
                  </li>
                  <li>
                    Enables visibility into ownership, health, and performance
                    at every level.
                  </li>
                </ul>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Operational Insight at Every Level
                </Typography>
                <ul>
                  <li>
                    Surfaces metrics like uptime, security score, escaped
                    defects, and more.
                  </li>
                  <li>
                    All metrics for a given unit at any level visible in a
                    single pane of glass.
                  </li>
                  <li>Aggregate metrics visible to organization leaders.</li>
                </ul>
              </Box>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  One-Click Project Creation
                </Typography>
                <ul>
                  <li>
                    Enables instant creation of new API services and more using
                    standardized templates.
                  </li>
                  <li>
                    Supports deployment and hosting of simple AI Vibe-coded
                    projects with one click.
                  </li>
                  <li>
                    Hosted in WellSky’s GCP environment, protected by Okta and
                    Cloud Armor.
                  </li>
                  <li>
                    SkyStage will host the AI Projects Catalog, showcasing
                    reusable Vibe-coded apps and AI agents.
                  </li>
                </ul>
              </Box>
            </InfoCard>
          </SectionBox>

          {/* Core Capabilities */}
          <SectionBox>
            <InfoCard title="⚙️ Core Capabilities" divider>
              {(() => {
                const rows = [
                  {
                    label: 'Software Catalog',
                    catalogLink: '/catalog',
                    detailUrl:
                      'https://wellsky.atlassian.net/wiki/spaces/ME/pages/1782088736',
                    description:
                      'Visualize all software assets and their relationships',
                  },
                  {
                    label: 'Operational Metrics',
                    catalogLink: '/catalog',
                    detailUrl:
                      'https://wellsky.atlassian.net/wiki/spaces/ME/pages/1782088736',
                    description:
                      'View uptime, vulnerabilities, and ownership at every level',
                  },
                  {
                    label: 'Project Creation',
                    catalogLink: '/create',
                    detailUrl:
                      'https://wellsky.atlassian.net/wiki/spaces/ME/pages/1781958247',
                    description:
                      'One-click creation of new services, projects, and AI Vibe-coded apps',
                  },
                  {
                    label: 'AI Project Catalog',
                    catalogLink: '/create',
                    detailUrl:
                      'https://wellsky.atlassian.net/wiki/spaces/ME/pages/1781958247',
                    description:
                      'Discover and reuse hosted AI agents and applications',
                  },
                ];
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        <th
                          style={{
                            padding: 12,
                            border: '1px solid #ddd',
                            textAlign: 'left',
                          }}
                        >
                          Capability
                        </th>
                        <th
                          style={{
                            padding: 12,
                            border: '1px solid #ddd',
                            textAlign: 'left',
                          }}
                        >
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(row => (
                        <tr key={row.label}>
                          <td style={{ padding: 12, border: '1px solid #ddd' }}>
                            <Link to={row.catalogLink}>{row.label}</Link>
                          </td>
                          <td style={{ padding: 12, border: '1px solid #ddd' }}>
                            {row.description}{' '}
                            <a
                              href={row.detailUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                marginLeft: 8,
                                fontSize: 14,
                                textDecoration: 'underline',
                                color: '#1976d2',
                              }}
                            >
                              More details
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </InfoCard>
          </SectionBox>

          {/* Strategic Impact */}
          <SectionBox>
            <InfoCard title="📈 Strategic Impact" divider>
              <Typography paragraph>
                SkyStage is not just a tool—it’s a platform for transformation:
              </Typography>
              <ul>
                <li>
                  Enables bottom-up alignment with MBOs by empowering teams to
                  contribute workflows that matter.
                </li>
                <li>
                  Supports cross-functional collaboration by making software and
                  its context visible to all stakeholders.
                </li>
                <li>
                  Drives efficiency and innovation by reducing manual effort and
                  surfacing reusable assets.
                </li>
                <li>
                  Provides accountability and insight for leadership to track
                  progress and impact.
                </li>
              </ul>
            </InfoCard>
          </SectionBox>
        </Box>
      </Content>
    </Page>
  );
};
